// Screenshot helpers for Otto feedback: one-click capture of exactly what the user sees, plus
// upload/paste of an image file. Everything is downscaled + re-encoded to a compact JPEG data URL so a
// stored screenshot is ~100–500 KB rather than a multi-MB PNG. The image is human-review context only
// (shown in the tracing dashboard) — it is NEVER sent to OpenAI or any interpret/resolve path.
//
// The one-click capture uses the Screen Capture API (getDisplayMedia): it grabs a single frame of the
// REAL composited tab, so it is pixel-perfect — the exact scroll position (including the chat's inner
// scroll), real fonts/shadows, and charts/images are all faithful to what the user saw. This is why we
// dropped the earlier dom-to-image approach (a DOM clone that reset inner scroll and mangled CSS).

const MAX_DIM = 1600;
const QUALITY = 0.7;

// Downscale (longest side -> maxDim) and re-encode as JPEG. Returns the input unchanged on any failure
// (best-effort — a larger image beats no image). JPEG has no alpha, so paint white first. Used by the
// upload/paste path (the capture path scales inline while drawing the video frame).
export function compress(dataUrl, { maxDim = MAX_DIM, quality = QUALITY } = {}) {
    return new Promise(resolve => {
        const img = new Image();
        img.onload = () => {
            try {
                const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
                const w = Math.max(1, Math.round(img.width * scale));
                const h = Math.max(1, Math.round(img.height * scale));
                const canvas = document.createElement("canvas");
                canvas.width = w;
                canvas.height = h;
                const ctx = canvas.getContext("2d");
                ctx.fillStyle = "#ffffff";
                ctx.fillRect(0, 0, w, h);
                ctx.drawImage(img, 0, 0, w, h);
                resolve(canvas.toDataURL("image/jpeg", quality));
            } catch (e) {
                resolve(dataUrl);
            }
        };
        img.onerror = () => resolve(dataUrl);
        img.src = dataUrl;
    });
}

// Draw a single frame from a display-capture MediaStream into a downscaled JPEG data URL. Uses an
// off-DOM <video> (broadest support) and waits for a real painted frame before drawing.
function grabFrame(stream, { maxDim = MAX_DIM, quality = QUALITY } = {}) {
    return new Promise((resolve, reject) => {
        const video = document.createElement("video");
        video.muted = true;
        video.playsInline = true;
        video.srcObject = stream;

        const draw = () => {
            try {
                const vw = video.videoWidth;
                const vh = video.videoHeight;
                if (!vw || !vh) {
                    reject(new Error("no frame"));
                    return;
                }
                const scale = Math.min(1, maxDim / Math.max(vw, vh));
                const w = Math.max(1, Math.round(vw * scale));
                const h = Math.max(1, Math.round(vh * scale));
                const canvas = document.createElement("canvas");
                canvas.width = w;
                canvas.height = h;
                canvas.getContext("2d").drawImage(video, 0, 0, w, h);
                resolve(canvas.toDataURL("image/jpeg", quality));
            } catch (e) {
                reject(e);
            } finally {
                video.srcObject = null;
            }
        };

        video.onloadedmetadata = () => {
            const ready = () => {
                // requestVideoFrameCallback guarantees a painted frame; fall back to a short delay.
                if (typeof video.requestVideoFrameCallback === "function") {
                    video.requestVideoFrameCallback(() => draw());
                } else {
                    setTimeout(draw, 150);
                }
            };
            const played = video.play();
            if (played && typeof played.then === "function") played.then(ready).catch(() => ready());
            else ready();
        };
        video.onerror = () => {
            video.srcObject = null;
            reject(new Error("video error"));
        };
    });
}

// Capture exactly what the user sees, as a compressed JPEG data URL. Prompts the browser's screen-share
// picker (preferring the current tab), grabs one frame, then immediately stops the track so the sharing
// indicator disappears. Resolves null if the API is unavailable or the user cancels the picker — the
// caller then just sends the feedback without a screenshot.
export async function captureDashboard(opts = {}) {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getDisplayMedia) return null;
    let stream = null;
    try {
        stream = await navigator.mediaDevices.getDisplayMedia({
            video: { displaySurface: "browser" },
            audio: false,
            // Chromium hint: default/limit the picker to THIS tab -> a simple "Share this tab?" prompt.
            preferCurrentTab: true
        });
        return await grabFrame(stream, opts);
    } catch (e) {
        return null; // user cancelled, denied, or unsupported
    } finally {
        if (stream) stream.getTracks().forEach(t => t.stop());
    }
}

// Read a File/Blob into a data URL.
export function fileToDataUrl(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(reader.error || new Error("read failed"));
        reader.readAsDataURL(file);
    });
}

// Read a File/Blob straight to a compressed JPEG data URL.
export async function fileToScreenshot(file) {
    return compress(await fileToDataUrl(file));
}

// Pull the first image out of a clipboard paste event, or null if none.
export function imageFromClipboard(event) {
    const items = event.clipboardData && event.clipboardData.items;
    if (!items) return null;
    for (const item of items) {
        if (item.type && item.type.startsWith("image/")) return item.getAsFile();
    }
    return null;
}
